import { b2x } from '@b2x/react/src';
import React from 'react';

import { AccountOffcanvasProps, useAccountOffcanvas } from './AccountOffcanvas';
import { CartOffcanvasProps, useCartOffcanvas } from './CartOffcanvas';

interface AppContextInterface {
  AccountOffcanvas: React.FunctionComponentElement<AccountOffcanvasProps>;
  CartOffcanvas: React.FunctionComponentElement<CartOffcanvasProps>;
  headerCheckout: boolean;
  hideFooter: boolean;
  hideHeader: boolean;
  thingsToLoadBeforeAppReady: Record<string, unknown>;
}

export const [AppContextProvider, useAppContext] = b2x.createContext<AppContextInterface>('AppContext');

interface AppStaticContextInterface {
  setHeaderCheckout: React.Dispatch<React.SetStateAction<boolean>>;
  setHideFooter: React.Dispatch<React.SetStateAction<boolean>>;
  setHideHeader: React.Dispatch<React.SetStateAction<boolean>>;
  setThingsToLoadBeforeAppReady: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  showAccountOffcanvas(): void;
  showCartOffcanvas(): void;
}

export const [AppStaticContextProvider, useAppStaticContext] =
  b2x.createContext<AppStaticContextInterface>('AppStaticContext');

interface UseAppContextInitializerProps {}

const useAppContextInitializer = (props: UseAppContextInitializerProps) => {
  const [AccountOffcanvas, showAccountOffcanvas] = useAccountOffcanvas();
  const [hideHeader, setHideHeader] = React.useState<boolean>(false);
  const [hideFooter, setHideFooter] = React.useState<boolean>(false);
  const [CartOffcanvas, showCartOffcanvas] = useCartOffcanvas();
  const [headerCheckout, setHeaderCheckout] = React.useState<boolean>(false);
  const [thingsToLoadBeforeAppReady, setThingsToLoadBeforeAppReady] = React.useState<Record<string, unknown>>({});

  const appContext: AppContextInterface = React.useMemo(
    () => ({
      AccountOffcanvas,
      CartOffcanvas,
      headerCheckout,
      hideFooter,
      hideHeader,
      thingsToLoadBeforeAppReady,
    }),
    [AccountOffcanvas, CartOffcanvas, headerCheckout, thingsToLoadBeforeAppReady, hideFooter, hideHeader],
  );

  const appStaticContext: AppStaticContextInterface = React.useMemo(
    () => ({
      setHeaderCheckout,
      setHideFooter,
      setHideHeader,
      setThingsToLoadBeforeAppReady,
      showAccountOffcanvas,
      showCartOffcanvas,
    }),
    [showAccountOffcanvas, showCartOffcanvas, setHideFooter, setHideHeader],
  );

  return {
    AppContextProvider,
    AppStaticContextProvider,
    appContext,
    appStaticContext,
  };
};

export interface AppContextProps extends UseAppContextInitializerProps {
  children:
    | React.ReactNode
    | ((appContext: AppContextInterface, appStaticContext: AppStaticContextInterface) => React.ReactNode);
}

export const AppContext = ({ children, ...otherProps }: AppContextProps) => {
  const appContextInitializer = useAppContextInitializer(otherProps);
  return (
    <appContextInitializer.AppContextProvider value={appContextInitializer.appContext}>
      <appContextInitializer.AppStaticContextProvider value={appContextInitializer.appStaticContext}>
        {typeof children === 'function'
          ? children(appContextInitializer.appContext, appContextInitializer.appStaticContext)
          : children}
      </appContextInitializer.AppStaticContextProvider>
    </appContextInitializer.AppContextProvider>
  );
};
